/**
 * URL building utilities for tenant-aware routing
 */

// Organization slug validation regex - alphanumeric with hyphens, 3-50 chars
export const ORG_SLUG_REGEX = /^[a-z0-9-]{3,50}$/;

/**
 * Validate an organization slug
 * @param {string} slug - The slug to validate
 * @returns {boolean} True if valid
 */
export function validateOrgSlug(slug) {
  if (!slug) return false;
  return ORG_SLUG_REGEX.test(slug);
}

import { getCurrentClientSlug, getCurrentBranchSlug, getCurrentParentSlug } from './subdomain';

const getDefaultOrgSlug = () => {
  const branch = getCurrentBranchSlug();
  if (branch && validateOrgSlug(branch)) {
    return branch;
  }
  const client = getCurrentClientSlug();
  if (client && validateOrgSlug(client)) {
    return client;
  }
  return null;
};

const resolveTenantInput = (input, branch) => {
  if (typeof input === 'object' && input !== null) {
    return {
      clientSlug:
        input.clientSlug ||
        input.orgSlug ||
        input.organizationSlug ||
        getDefaultOrgSlug() ||
        getCurrentClientSlug(),
      branchSlug: input.branchSlug || input.branch || getCurrentBranchSlug(),
      clientTenantSlug:
        input.clientTenantSlug ||
        input.clientTenant ||
        input.parentClientSlug ||
        getCurrentClientSlug(),
      parentSlug: input.parentSlug || input.parent || getCurrentParentSlug(),
      path: input.path,
    };
  }

  return {
    clientSlug: input || getDefaultOrgSlug() || getCurrentClientSlug(),
    branchSlug: typeof branch === 'string' ? branch : getCurrentBranchSlug(),
    clientTenantSlug: getCurrentClientSlug(),
    parentSlug: getCurrentParentSlug(),
  };
};

/**
 * Build a tenant-specific URL.
 * Accepts a slug string or an options object `{ clientSlug, branchSlug, path }`.
 */
export function buildTenantUrl(orgOrOptions, path = '/', branchSlug) {
  const {
    clientSlug,
    branchSlug: resolvedBranch,
    clientTenantSlug,
    parentSlug: resolvedParent,
    path: explicitPath,
  } = resolveTenantInput(orgOrOptions, branchSlug);
  const targetPath = explicitPath !== undefined ? explicitPath : path;

  const baseDomain = import.meta.env.VITE_APP_BASE_DOMAIN || 'localhost';
  const port = import.meta.env.VITE_APP_DEV_PORT || '5173';
  const isLocal =
    baseDomain === 'localhost' ||
    baseDomain.includes('127.0.0.1') ||
    baseDomain.endsWith('local.test');

  if (!validateOrgSlug(clientSlug)) {
    console.error(`[buildTenantUrl] Invalid org slug: ${clientSlug}`);
    return targetPath;
  }

  const normalizedPath = targetPath.startsWith('/') ? targetPath : `/${targetPath}`;

  const composeHost = () => {
    const hostParts = [];
    if (resolvedBranch && validateOrgSlug(resolvedBranch)) {
      // Join branch + client with hyphen as a single subdomain label
      // to match the backend's build_tenant_hostname format
      const clientLabel = clientTenantSlug && validateOrgSlug(clientTenantSlug)
        ? clientTenantSlug
        : clientSlug;
      // A client label equal to the branch is never right: it means the slug
      // was read from a branch-scoped source (or re-parsed from a host that
      // was already wrong) and would compose an unreachable "op.op" style
      // host. Fall back to the branch alone rather than doubling it.
      const clientLabelUsable =
        clientLabel &&
        validateOrgSlug(clientLabel) &&
        clientLabel.toLowerCase() !== resolvedBranch.toLowerCase();
      if (clientLabelUsable) {
        hostParts.push(`${resolvedBranch}-${clientLabel}`);
      } else {
        if (clientLabel && clientLabel.toLowerCase() === resolvedBranch.toLowerCase()) {
          console.warn(
            `[buildTenantUrl] client slug "${clientLabel}" equals branch slug — ` +
            'dropping it rather than composing a duplicated host',
          );
        }
        hostParts.push(resolvedBranch);
      }
    } else if (clientSlug && validateOrgSlug(clientSlug)) {
      hostParts.push(clientSlug);
    }

    // Same rule one level up: a parent identical to the label already present
    // duplicates it into "op.op".
    if (
      resolvedParent &&
      validateOrgSlug(resolvedParent) &&
      !hostParts.some((p) => p.toLowerCase() === resolvedParent.toLowerCase())
    ) {
      hostParts.push(resolvedParent);
    }

    if (!hostParts.length) {
      hostParts.push(clientSlug || 'tenant');
    }

    return hostParts.join('.');
  };

  if (isLocal) {
    const tenantHost = composeHost();
    const baseHost = baseDomain === 'localhost' || baseDomain.includes('127.0.0.1')
      ? `${tenantHost}.localhost`
      : `${tenantHost}.${baseDomain}`;
    // Follow however the dev server is actually being served. This was
    // hardcoded to http, which breaks once Vite runs with mkcert certs — the
    // browser lands on a port that only speaks TLS.
    const scheme =
      typeof window !== 'undefined' && window.location?.protocol === 'https:' ? 'https' : 'http';
    return `${scheme}://${baseHost}:${port}${normalizedPath}`;
  }

  const host = composeHost();
  return `https://${host}.${baseDomain}${normalizedPath}`;
}

/**
 * Build a platform (non-tenant) URL
 * @param {string} path - Path to append
 * @returns {string} The complete platform URL
 */
export function buildPlatformUrl(path = '/') {
  const baseDomain = import.meta.env.VITE_APP_BASE_DOMAIN || 'localhost';
  const port = import.meta.env.VITE_APP_DEV_PORT || '5173';
  const platformSubdomain = import.meta.env.VITE_APP_PLATFORM_SUBDOMAIN || 'demo-platform';
  const isLocal =
    baseDomain === 'localhost' ||
    baseDomain.includes('127.0.0.1') ||
    baseDomain.endsWith('local.test');

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (isLocal) {
    const host = baseDomain === 'localhost' || baseDomain.includes('127.0.0.1')
      ? 'localhost'
      : `${platformSubdomain}.${baseDomain}`;
    // Match however the dev server is served — see buildTenantUrl.
    const scheme =
      typeof window !== 'undefined' && window.location?.protocol === 'https:' ? 'https' : 'http';
    return `${scheme}://${host}:${port}${normalizedPath}`;
  } else {
    return `https://${baseDomain}${normalizedPath}`;
  }
}

/**
 * Extract organization slug from current URL
 * @returns {string|null} The org slug or null if not found
 */
export function getOrgSlugFromUrl() {
  const hostname = window.location.hostname;

  // Check for subdomain
  const parts = hostname.split('.');
  if (parts.length > 2 || (parts.length === 2 && (parts[1] === 'localhost' || parts[1] === 'local')) ) {
    const slug = parts[0];
    return validateOrgSlug(slug) ? slug : null;
  }

  // Check for query parameter fallback
  const params = new URLSearchParams(window.location.search);
  const slug = params.get('org') || params.get('slug');
  return slug && validateOrgSlug(slug) ? slug : null;
}

/**
 * Check if URL is allowed for redirect (security check)
 * @param {string} url - URL to check
 * @returns {boolean} True if URL is safe to redirect to
 */
export function isAllowedRedirectUrl(url) {
  if (!url) return false;

  try {
    const parsed = new URL(url);
    const allowedHosts = [
      'localhost',
      '127.0.0.1',
      '.miralabs.ai',
      '.mirabank.io'  // Add other allowed domains
    ];

    // Check if hostname matches allowed patterns
    const hostname = parsed.hostname;
    return allowedHosts.some(allowed => {
      if (allowed.startsWith('.')) {
        // Domain suffix check
        return hostname.endsWith(allowed.substring(1)) || hostname === allowed.substring(1);
      }
      return hostname === allowed;
    });
  } catch (e) {
    // Invalid URL
    return false;
  }
}

/**
 * Build auth callback URL with organization context
 * @param {string} orgSlug - Organization slug
 * @returns {string} Auth callback URL
 */
export function buildAuthCallbackUrl(orgSlug) {
  if (orgSlug && validateOrgSlug(orgSlug)) {
    return buildTenantUrl(orgSlug, '/auth/callback');
  }
  return buildPlatformUrl('/auth/callback');
}

/**
 * Get base domain for cookies
 * @returns {string} Base domain for setting cookies
 */
export function getCookieDomain() {
  const baseDomain = import.meta.env.VITE_APP_BASE_DOMAIN || 'localhost';

  // For localhost, don't set domain (allows port-based dev)
  if (baseDomain === 'localhost' || baseDomain.includes('127.0.0.1')) {
    return undefined;
  }

  // Use explicit cookie domain if configured (apex domain for cross-subdomain sharing)
  const cookieDomain = import.meta.env.VITE_APP_COOKIE_DOMAIN;
  if (cookieDomain) {
    return cookieDomain;
  }

  // Fallback to base domain
  return `.${baseDomain}`;
}
